import { ErrorModal, MesfModal, getMomentTz } from "@dexteel/mesf-core";
import {
	Box,
	Button,
	Checkbox,
	FormControlLabel,
	Typography,
} from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchEventsToCompare } from "../../hooks/useSearchEvents";
import { EventToCompare } from "../../models/Event";
import { EventModal } from "./EventModal";
import { PublishCalendarModal } from "./PublishCalendarModal";
import { ACTION_COLORS } from "./utils/confirmDraftEventsUtils";
import { EventRow } from "./utils/EventRow";
import { GridHeader } from "./utils/GridHeader";
import { MonthHeader } from "./utils/MonthHeader";
import { getDraftDivergenceSummary } from "../../utils/draftDivergenceSummary";

const moment = getMomentTz();

interface ConfirmDraftEventsModalProps {
	externalLoading: boolean;
	start: Date;
	end: Date;
	open: boolean;
	onHide: () => void;
	onConfirm: (
		confirmUntilEventId?: number | null,
		selectedEmails?: string,
		eventsPublished?: number,
		publisherNote?: string,
		addRecipientsToTopic?: boolean,
	) => void;
}

export const ConfirmDraftEventsModal = ({
	externalLoading,
	start,
	end,
	open,
	onHide,
	onConfirm,
}: ConfirmDraftEventsModalProps) => {
	const draftScrollRef = useRef<HTMLDivElement>(null);
	const mainScrollRef = useRef<HTMLDivElement>(null);
	const isScrollingRef = useRef(false);

	const [error, setError] = useState("");
	const [confirmModal, setConfirmModal] = useState<"partial" | null>(null);
	const [selectedDetailEventId, setSelectedDetailEventId] = useState<
		number | null
	>(null);
	const [selectedDetailMonthStart, setSelectedDetailMonthStart] =
		useState<Date | null>(null);
	const [selectedEventId, setSelectedEventId] = useState<number | null>(null);
	const [hoveredEventId, setHoveredEventId] = useState<number | null>(null);
	const [showBeforeAfter, setShowBeforeAfter] = useState(true);

	const {
		data,
		isLoading,
		error: e,
		isError,
	} = useSearchEventsToCompare({
		callSearch: open,
	});

	const { mainEvents, draftEvents } = data || {
		mainEvents: [],
		draftEvents: [],
	};

	const formattedMainEvents = mainEvents.map((me) => {
		const sameDraftEvent = draftEvents.find((de) => de.EventId === me.EventId);

		if (sameDraftEvent && me.ChangedFields) {
			const changedFieldsList = me.ChangedFields.split(",").map((f) =>
				f.trim(),
			);
			return {
				...me,
				...changedFieldsList.reduce(
					(acc, field) => {
						acc[`New${field}`] = sameDraftEvent[field as keyof EventToCompare];
						return acc;
					},
					{} as Record<string, EventToCompare[keyof EventToCompare]>,
				),
			};
		}
		return me;
	});

	const formattedDraftEvents = draftEvents.map((de) => {
		const sameMainEvent = mainEvents.find((me) => me.EventId === de.EventId);

		if (sameMainEvent && de.ChangedFields) {
			const changedFieldsList = de.ChangedFields.split(",").map((f) =>
				f.trim(),
			);
			return {
				...de,
				...changedFieldsList.reduce(
					(acc, field) => {
						acc[`Original${field}`] =
							sameMainEvent[field as keyof EventToCompare];
						return acc;
					},
					{} as Record<string, EventToCompare[keyof EventToCompare]>,
				),
			};
		}
		return de;
	});

	const stats = useMemo(() => {
		const summary = getDraftDivergenceSummary({
			mainEvents: formattedMainEvents,
			draftEvents: formattedDraftEvents,
		});
		const unchanged = formattedDraftEvents.filter(
			(e) => e.ActionTypeCode === null,
		).length;
		return { ...summary, unchanged };
	}, [formattedMainEvents, formattedDraftEvents]);

	const handleClose = () => {
		setSelectedEventId(null);
		onHide();
	};

	const handleRowSelect = (eventId: number) => {
		if (selectedEventId === eventId) {
			setSelectedEventId(null);
		} else {
			setSelectedEventId(eventId);
		}
	};

	const handleConfirmPartial = (
		selectedEmails: string,
		publisherNote: string | undefined,
		addRecipientsToTopic: boolean,
	) => {
		if (selectedEventId === null) return;
		onConfirm(
			selectedEventId,
			selectedEmails,
			eventsToPublishCount ?? 0,
			publisherNote,
			addRecipientsToTopic,
		);
	};

	const selectedDraftIndex =
		selectedEventId !== null
			? formattedDraftEvents.findIndex((e) => e.EventId === selectedEventId)
			: -1;

	const selectedDraftEvent =
		selectedDraftIndex !== -1 ? formattedDraftEvents[selectedDraftIndex] : null;

	const eventsToPublishCount =
		selectedDraftIndex === null ? 0 : selectedDraftIndex + 1;

	const handleScroll = (
		source: "draft" | "main",
		e: React.UIEvent<HTMLDivElement>,
	) => {
		if (isScrollingRef.current) return;

		const sourceElement = e.currentTarget;
		const targetElement =
			source === "draft" ? mainScrollRef.current : draftScrollRef.current;

		if (targetElement) {
			isScrollingRef.current = true;

			// Calculate scroll of source
			const maxScrollSource =
				sourceElement.scrollHeight - sourceElement.clientHeight;
			const scrollPercentage =
				maxScrollSource > 0 ? sourceElement.scrollTop / maxScrollSource : 0;

			// Aplly scroll to destiny
			const maxScrollTarget =
				targetElement.scrollHeight - targetElement.clientHeight;
			targetElement.scrollTop = scrollPercentage * maxScrollTarget;

			requestAnimationFrame(() => {
				isScrollingRef.current = false;
			});
		}
	};

	const hasChanges =
		stats.created > 0 ||
		stats.deleted > 0 ||
		stats.updated > 0 ||
		stats.moved > 0;

	useEffect(() => {
		// Every time the selected row (draft) changes, recalculates the scroll of main.
		if (isScrollingRef.current) return;

		if (draftScrollRef.current && mainScrollRef.current) {
			isScrollingRef.current = true;

			requestAnimationFrame(() => {
				if (draftScrollRef.current && mainScrollRef.current) {
					// Calculate draft scroll
					const maxScrollSource =
						draftScrollRef.current.scrollHeight -
						draftScrollRef.current.clientHeight;
					const scrollPercentage =
						maxScrollSource > 0
							? draftScrollRef.current.scrollTop / maxScrollSource
							: 0;

					// Aplly draft scroll to main
					const maxScrollTarget =
						mainScrollRef.current.scrollHeight -
						mainScrollRef.current.clientHeight;
					mainScrollRef.current.scrollTop = scrollPercentage * maxScrollTarget;

					requestAnimationFrame(() => {
						isScrollingRef.current = false;
					});
				}
			});
		}
	}, [selectedEventId]);

	useEffect(() => {
		if (e && isError) {
			setError(e.message);
		}
	}, [e, isError]);

	return (
		<>
			<MesfModal
				open={open}
				handleClose={handleClose}
				title="Compare Draft vs Main Events"
				maxWidth="xl"
				fullWidth
			>
				<MesfModal.Content dividers>
					<Box
						sx={{
							display: "flex",
							justifyContent: "space-between",
							gap: 3,
							mb: 1,
							flexWrap: "wrap",
						}}
					>
						<Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
							{[...ACTION_COLORS].map((action) => {
								const statsKey = action.name as keyof typeof stats;
								const actionLength = stats[statsKey];

								return (
									<Box
										key={action.name}
										sx={{ display: "flex", alignItems: "center", gap: 1 }}
									>
										<Box
											sx={{
												width: 12,
												height: 12,
												backgroundColor: action.color,
												borderRadius: 1,
											}}
										/>
										<Typography variant="body2">
											<strong>{actionLength}</strong>{" "}
											{action.name === "updated" || action.name === "unchanged"
												? " "
												: "to "}
											{action.name}
										</Typography>
									</Box>
								);
							})}
						</Box>
						<FormControlLabel
							control={
								<Checkbox
									checked={showBeforeAfter}
									onChange={(e) => setShowBeforeAfter(e.target.checked)}
									size="small"
								/>
							}
							label={
								<Typography variant="body2">
									Show Before/After values
								</Typography>
							}
						/>
					</Box>

					{!hasChanges && !isLoading ? (
						<Typography color="textSecondary" align="center" sx={{ py: 4 }}>
							No changes detected between Draft and Main
						</Typography>
					) : (
						<Box
							sx={{ display: "grid", gridTemplateColumns: "0.7fr 1fr", gap: 2 }}
						>
							{/* Main Events Column */}
							<Box>
								<Box
									sx={{
										py: 1,
										px: 2,
										backgroundColor: "success.main",
										color: "success.contrastText",
										borderRadius: "4px 4px 0 0",
									}}
								>
									<Typography variant="subtitle2" fontWeight={600}>
										Main (Current)
									</Typography>
								</Box>
								<GridHeader />
								<Box
									ref={mainScrollRef}
									onScroll={(e) => handleScroll("main", e)}
									sx={{ maxHeight: "60vh", overflowY: "auto" }}
								>
									{formattedMainEvents.length === 0 ? (
										<Typography
											color="textSecondary"
											align="center"
											sx={{ py: 2 }}
										>
											No events in Main
										</Typography>
									) : (
										(() => {
											let lastRenderedMonth: string | null = null;

											return formattedMainEvents
												.map((event, index) => {
													if (!selectedDraftEvent) {
														const showMonthHeader =
															lastRenderedMonth === null ||
															moment(event.Start).format("MMMM YYYY") !==
																lastRenderedMonth;

														if (showMonthHeader) {
															lastRenderedMonth = moment(event.Start).format(
																"MMMM YYYY",
															);
														}

														return (
															<Box key={event.EventId}>
																{showMonthHeader && (
																	<MonthHeader
																		monthLabel={moment(event.Start).format(
																			"MMMM",
																		)}
																	/>
																)}
																<EventRow
																	event={event}
																	isHovered={hoveredEventId === event.EventId}
																	onHoverChange={(hovered: boolean) =>
																		setHoveredEventId(
																			hovered ? event.EventId : null,
																		)
																	}
																	openDetails={() =>
																		setSelectedDetailEventId(event.EventId)
																	}
																	showBeforeAfter={showBeforeAfter}
																/>
															</Box>
														);
													}

													const draftIndex = formattedDraftEvents.findIndex(
														(e) => e.EventId === event.EventId,
													);

													// If event exists on draft, but is after the selected, don't show.
													if (draftIndex > selectedDraftIndex) {
														return null;
													}

													// If event not exists on draft, but conceptually is after, don't show.
													const selectedMainIndex =
														formattedMainEvents.findIndex(
															(e) => e.EventId === selectedDraftEvent.EventId,
														);
													const currentMainIndex =
														formattedMainEvents.findIndex(
															(e) => e.EventId === event.EventId,
														);
													if (
														draftIndex === -1 &&
														selectedMainIndex !== -1 &&
														currentMainIndex > selectedMainIndex
													) {
														return null;
													}

													const showMonthHeader =
														lastRenderedMonth === null ||
														moment(event.Start).format("MMMM YYYY") !==
															lastRenderedMonth;

													if (showMonthHeader) {
														lastRenderedMonth = moment(event.Start).format(
															"MMMM YYYY",
														);
													}

													return (
														<Box key={event.EventId}>
															{showMonthHeader && (
																<MonthHeader
																	monthLabel={moment(event.Start).format(
																		"MMMM",
																	)}
																/>
															)}
															<EventRow
																event={event}
																isHovered={hoveredEventId === event.EventId}
																onHoverChange={(hovered: boolean) =>
																	setHoveredEventId(
																		hovered ? event.EventId : null,
																	)
																}
																openDetails={() => {
																	setSelectedDetailEventId(event.EventId);
																	setSelectedDetailMonthStart(
																		moment(event.Start)
																			.startOf("month")
																			.toDate(),
																	);
																}}
																showBeforeAfter={showBeforeAfter}
															/>
														</Box>
													);
												})
												.filter(Boolean);
										})()
									)}
									{selectedDraftEvent != null ? (
										<Typography
											variant="body2"
											color="textSecondary"
											align="center"
											sx={{ py: 2 }}
										>
											All the events after{" '"}
											{selectedDraftEvent.SectionName ??
												selectedDraftEvent.EventTypeName}
											{"' "}
											will be deleted on Main.
										</Typography>
									) : null}
								</Box>
							</Box>

							{/* Draft Events Column */}
							<Box>
								<Box
									sx={{
										py: 1,
										px: 2,
										backgroundColor: "primary.main",
										color: "primary.contrastText",
										borderRadius: "4px 4px 0 0",
									}}
								>
									<Typography variant="subtitle2" fontWeight={600}>
										Draft (Pending)
									</Typography>
								</Box>
								<GridHeader showAction showCheckbox showChangedFields />
								<Box
									ref={draftScrollRef}
									onScroll={(e) => handleScroll("draft", e)}
									sx={{ maxHeight: "60vh", overflowY: "auto" }}
								>
									{draftEvents.length === 0 ? (
										<Typography
											color="textSecondary"
											align="center"
											sx={{ py: 2 }}
										>
											No events in Draft
										</Typography>
									) : (
										formattedDraftEvents.map((event, index) => {
											const showMonthHeader =
												index === 0 ||
												moment(event.Start).format("MMMM YYYY") !==
													moment(formattedDraftEvents[index - 1].Start).format(
														"MMMM YYYY",
													);

											return (
												<Box key={`${event.EventId}-${index}`}>
													{showMonthHeader && (
														<MonthHeader
															monthLabel={moment(event.Start).format("MMMM")}
														/>
													)}
													<EventRow
														event={event}
														selected={
															selectedDraftIndex !== null &&
															index <= selectedDraftIndex
														}
														onSelect={() => handleRowSelect(event.EventId)}
														isHovered={hoveredEventId === event.EventId}
														onHoverChange={(hovered: boolean) =>
															setHoveredEventId(hovered ? event.EventId : null)
														}
														openDetails={() =>
															setSelectedDetailEventId(event.EventId)
														}
														showAction
														showChangedFields
														showBeforeAfter={showBeforeAfter}
													/>
												</Box>
											);
										})
									)}
								</Box>
							</Box>
						</Box>
					)}

					{/* Legend */}
					<Box
						sx={{
							mt: 2,
							pt: 2,
							borderTop: "1px solid",
							borderColor: "divider",
						}}
					>
						<Typography variant="caption" color="textSecondary">
							<strong>Legend:</strong> Events with the same color band belong to
							the same synchronized group. When groups differ between columns,
							it indicates sequence divergence.
						</Typography>
					</Box>
				</MesfModal.Content>

				<MesfModal.Actions sx={{ px: 3, py: 2 }}>
					<Box
						sx={{
							display: "flex",
							justifyContent: "space-between",
							width: "100%",
						}}
					>
						<Button
							variant="outlined"
							onClick={handleClose}
							disabled={isLoading || externalLoading}
						>
							Cancel
						</Button>
						<Box sx={{ display: "flex", gap: 2 }}>
							{selectedEventId !== null ? (
								<Button
									variant="contained"
									color="primary"
									onClick={() => setConfirmModal("partial")}
									disabled={externalLoading || isLoading}
									data-test-id="publish-selected-button"
								>
									Publish Selected ({eventsToPublishCount}{" "}
									{eventsToPublishCount === 1 ? "event" : "events"})
								</Button>
							) : (
								<Typography>Select an event to publish.</Typography>
							)}
						</Box>
					</Box>
				</MesfModal.Actions>
			</MesfModal>

			<PublishCalendarModal
				open={confirmModal === "partial"}
				title="Publish Calendar"
				message={`Are you sure you want to publish the first ${eventsToPublishCount} event(s)?\n\nAll events up to and including the selected row will be published.`}
				resource="CALENDAR_PUBLISH"
				loading={externalLoading}
				onClose={() => setConfirmModal(null)}
				onConfirm={async (
					selectedEmails,
					publisherNote,
					addRecipientsToTopic,
				) => {
					await handleConfirmPartial(
						selectedEmails.join(";"),
						publisherNote,
						addRecipientsToTopic,
					);
					handleClose();
				}}
			/>

			<ErrorModal error={error} onHide={() => setError("")} />
			<EventModal
				open={selectedDetailEventId !== null}
				onHide={() => {
					setSelectedDetailEventId(null);
					setSelectedDetailMonthStart(null);
				}}
				selectedEventId={selectedDetailEventId}
				monthStart={selectedDetailMonthStart}
				isDraftMode={true}
				disabled
			/>
		</>
	);
};
